/**
 * Per-org secrets — one row per defined SecretField.
 *
 * The tenant half of the same list Security shows at the platform layer, and
 * deliberately the same table: a key either has a value of its own here or it
 * falls through to the platform one, and that fall-through is the only thing
 * this page exists to make visible.
 *
 * It was a stack of forms with a permanently open input per key and no table at
 * all, which showed every key as equally in need of attention and never showed
 * which layer a value would actually come from.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@optum/api-client/client';
import {
  deleteOrgSecret,
  listOrgSecrets,
} from '@optum/api-client/endpoints/organization-secrets';
import type { OrgSecretEntry } from '@optum/api-client/types';

import { Button } from '../../ui/Button.js';
import { CredentialTable, type CredentialRow } from '../../ui/CredentialTable.js';
import { formatSecretTarget } from '../../components/forms/SetSecretValueForm.js';
import { useRecordPanel } from '../../components/shell/record.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { useStepUp } from '../../lib/step-up.js';

export function OrgSecretsEditor({ organizationId }: { organizationId: string }) {
  const query = useQuery({
    queryKey: qk.orgSecrets(organizationId),
    queryFn: () => listOrgSecrets(organizationId),
  });

  if (query.isPending) return <LoadingBlock />;
  if (query.isError) {
    return (
      <ErrorBlock message={query.error instanceof Error ? query.error.message : 'Failed to load'} />
    );
  }
  const entries = query.data ?? [];
  if (entries.length === 0) {
    return <EmptyBlock>No secret fields defined yet — define them in Security first.</EmptyBlock>;
  }

  const set = entries.filter((e) => e.is_set).length;
  return (
    <CredentialTable
      rows={entries.map<CredentialRow>((entry) => ({
        id: entry.field_key,
        name: entry.field_key,
        sub: entry.label,
        scope: entry.is_secret ? `${entry.type} · secret` : entry.type,
        status: entry.is_set ? { label: 'org', tone: 'blue' } : { label: 'platform', tone: 'warn' },
        actions: <SecretActions organizationId={organizationId} entry={entry} />,
      }))}
      count={`${entries.length} secrets · ${set} set for this tenant`}
      hint="an unset key falls back to the platform value"
    />
  );
}

function SecretActions({
  organizationId,
  entry,
}: {
  organizationId: string;
  entry: OrgSecretEntry;
}) {
  const panel = useRecordPanel();
  const qc = useQueryClient();
  const toast = useToast();
  // Secret writes demand a recent sign-in; the step-up wrapper asks the
  // operator to sign in again when the API refuses.
  const [guardedDelete, deleteDialog] = useStepUp(deleteOrgSecret);


  const clear = useMutation({
    mutationFn: () => guardedDelete(organizationId, entry.field_key),
    onSuccess: () => {
      toast.success(`Cleared ${entry.field_key}`);
      qc.invalidateQueries({ queryKey: qk.orgSecrets(organizationId) });
    },
    onError: (e) => toast.error('Clear failed', e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <>
      {deleteDialog}
      <Button
        size="sm"
        variant="secondary"
        onClick={() => panel.openForm({ name: 'set-secret', id: formatSecretTarget({ layer: 'org', orgId: organizationId, key: entry.field_key }) })}
      >
        {entry.is_set ? 'Replace' : 'Set value'}
      </Button>
      {entry.is_set && (
        <Button
          size="sm"
          variant="danger"
          onClick={() => clear.mutate()}
          disabled={clear.isPending}
        >
          Clear
        </Button>
      )}
    </>
  );
}
